import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useMutation, useQuery } from '@apollo/client/react';
import { CREATE_MEAL, GET_MEALS } from '../../graphql/nutrition';
import DirectUpload from '../../components/shared/DirectUpload';

const TIME_OF_DAY_OPTIONS = [
  'BREAKFAST',
  'MORNING SNACK',
  'BRUNCH',
  'LUNCH',
  'AFTERNOON SNACK',
  'AFTERNOON MEAL',
  'DINNER',
];

const DEFAULT_DAY_MEALS = ['BREAKFAST', 'MORNING SNACK', 'LUNCH', 'DINNER'];
const ADD_MEAL_SEQUENCE = ['BREAKFAST', 'LUNCH', 'DINNER'];
const MIN_VISIBLE_DAYS = 7;

const TIME_LABELS = {
  BREAKFAST: { title: 'Breakfast', time: '7:00 AM - 9:00 AM' },
  'MORNING SNACK': { title: 'Morning Snack', time: '10:00 AM - 11:00 AM' },
  BRUNCH: { title: 'Brunch', time: '10:00 AM - 12:00 PM' },
  LUNCH: { title: 'Lunch', time: '1:00 PM - 2:00 PM' },
  'AFTERNOON SNACK': { title: 'Afternoon Snack', time: '3:00 PM - 4:00 PM' },
  'AFTERNOON MEAL': { title: 'Afternoon Meal', time: '3:00 PM - 4:30 PM' },
  DINNER: { title: 'Dinner', time: '6:00 PM - 7:30 PM' },
};

const startOfLocalDay = (value) => {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

const dateForDay = (day, weekStart) => {
  const date = new Date(weekStart);
  date.setDate(weekStart.getDate() + day - 1);
  return date;
};

const formatDateRange = (days, weekStart) => {
  const first = dateForDay(days[0] || 1, weekStart);
  const last = dateForDay(days[days.length - 1] || 7, weekStart);
  return `${first.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })} - ${last.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

const formatDayDate = (day, weekStart) =>
  dateForDay(day, weekStart).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const nutritionFor = (event) => {
  const macros = event?.meal?.macros;
  return {
    calories: macros?.calories ?? null,
    protein: macros?.proteinGrams ?? null,
    carbs: macros?.carbGrams ?? null,
    fats: macros?.fatGrams ?? null,
  };
};

const formatMacro = (value, suffix) =>
  value == null || Number.isNaN(Number(value)) ? '-' : `${value} ${suffix}`;

const normalizeEvent = (event = {}) => ({
  mealId: event.mealId || event.meal?._id || null,
  meal: event.meal && typeof event.meal === 'object' ? event.meal : null,
  day: event.day || 1,
  timeOfDay: event.timeOfDay || 'BREAKFAST',
});

const toDayNumber = (day) => {
  const parsed = parseInt(day, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getEventSortValue = (event) => {
  const optionIndex = TIME_OF_DAY_OPTIONS.indexOf(event.timeOfDay);
  return optionIndex === -1 ? TIME_OF_DAY_OPTIONS.length : optionIndex;
};

const getNextMealTimeOfDay = (events, day) => {
  const usedTimes = new Set(
    (events || [])
      .filter((event) => toDayNumber(event.day) === day)
      .map((event) => event.timeOfDay)
  );

  return (
    ADD_MEAL_SEQUENCE.find((timeOfDay) => !usedTimes.has(timeOfDay)) ||
    TIME_OF_DAY_OPTIONS.find((timeOfDay) => !usedTimes.has(timeOfDay)) ||
    'BREAKFAST'
  );
};

export default function MealPlanScheduleEditor({ mealPlan, onSave }) {
  const [events, setEvents] = useState(
    (mealPlan.events || []).map(normalizeEvent)
  );
  const [view, setView] = useState('table');
  const [visibleStartDay, setVisibleStartDay] = useState(1);
  const [createDialog, setCreateDialog] = useState({
    open: false,
    onCreated: null,
  });
  const [newMeal, setNewMeal] = useState({
    title: '',
    desc: '',
    coverImages: [],
  });
  const { data: mealsData, refetch: refetchMeals } = useQuery(GET_MEALS, {
    variables: { input: { limit: 100 } },
  });
  const [createMeal, { loading: creatingMeal }] = useMutation(CREATE_MEAL);
  const weekStart = useMemo(
    () => startOfLocalDay(mealPlan.startedAt || mealPlan.createdAt),
    [mealPlan.createdAt, mealPlan.startedAt]
  );
  const mealOptions = mealsData?.meals || [];

  useEffect(() => {
    setEvents((mealPlan.events || []).map(normalizeEvent));
  }, [mealPlan]);

  const days = useMemo(() => {
    const dayNumbers = Array.from(
      new Set((events || []).map((event) => toDayNumber(event.day)))
    ).sort((a, b) => a - b);
    const maxDay = Math.max(MIN_VISIBLE_DAYS, ...dayNumbers);

    return Array.from({ length: maxDay }, (_, index) => index + 1);
  }, [events]);

  const visibleDays = useMemo(() => {
    const maxStartDay = Math.max(1, days.length - MIN_VISIBLE_DAYS + 1);
    const startDay = Math.min(visibleStartDay, maxStartDay);

    return days.slice(startDay - 1, startDay - 1 + MIN_VISIBLE_DAYS);
  }, [days, visibleStartDay]);

  const rows = useMemo(() => {
    const times = Array.from(
      new Set([
        ...DEFAULT_DAY_MEALS,
        ...(events || []).map((event) => event.timeOfDay),
      ])
    );

    return times.sort((a, b) => {
      const aIndex = TIME_OF_DAY_OPTIONS.indexOf(a);
      const bIndex = TIME_OF_DAY_OPTIONS.indexOf(b);
      return (
        (aIndex === -1 ? TIME_OF_DAY_OPTIONS.length : aIndex) -
        (bIndex === -1 ? TIME_OF_DAY_OPTIONS.length : bIndex)
      );
    });
  }, [events]);

  const handleMealSelect = (day, timeOfDay, selectedMeal) => {
    setEvents((currentEvents) => {
      const eventIndex = currentEvents.findIndex(
        (event) =>
          toDayNumber(event.day) === day && event.timeOfDay === timeOfDay
      );

      if (eventIndex === -1) {
        return [
          ...currentEvents,
          normalizeEvent({
            day,
            timeOfDay,
            mealId: selectedMeal?._id || null,
            meal: selectedMeal || null,
          }),
        ];
      }

      return currentEvents.map((event, index) =>
        index === eventIndex
          ? {
              ...event,
              mealId: selectedMeal?._id || null,
              meal: selectedMeal || null,
            }
          : event
      );
    });
  };

  const handleEventMealSelect = (index, selectedMeal) => {
    setEvents((currentEvents) =>
      currentEvents.map((event, eventIndex) =>
        eventIndex === index
          ? {
              ...event,
              mealId: selectedMeal?._id || null,
              meal: selectedMeal || null,
            }
          : event
      )
    );
  };

  const handleEventTimeOfDayChange = (index, timeOfDay) => {
    setEvents((currentEvents) =>
      currentEvents.map((event, eventIndex) =>
        eventIndex === index
          ? {
              ...event,
              timeOfDay,
            }
          : event
      )
    );
  };

  const handleAddEvent = (day = days[0] || 1) => {
    setEvents((currentEvents) => {
      const normalizedDay = toDayNumber(day);
      const timeOfDay = getNextMealTimeOfDay(currentEvents, normalizedDay);

      return [
        ...currentEvents,
        normalizeEvent({ day: normalizedDay, timeOfDay }),
      ];
    });
  };

  const handleAddDay = () => {
    setEvents((currentEvents) => {
      const maxDay = currentEvents.reduce((result, event) => {
        const day = toDayNumber(event.day);
        return day > result ? day : result;
      }, MIN_VISIBLE_DAYS);
      const nextDay = maxDay + 1;
      setVisibleStartDay(Math.max(1, nextDay - MIN_VISIBLE_DAYS + 1));

      return [
        ...currentEvents,
        ...DEFAULT_DAY_MEALS.map((timeOfDay) =>
          normalizeEvent({ day: nextDay, timeOfDay })
        ),
      ];
    });
  };

  const handleCopyWeek = () => {
    setEvents((currentEvents) => {
      const maxDay = currentEvents.reduce((result, event) => {
        const day = toDayNumber(event.day);
        return day > result ? day : result;
      }, MIN_VISIBLE_DAYS);
      const sourceDays = Array.from(
        new Set(currentEvents.map((event) => toDayNumber(event.day)))
      ).sort((a, b) => a - b);
      const copiedLastDay = maxDay + sourceDays.length;
      setVisibleStartDay(Math.max(1, copiedLastDay - MIN_VISIBLE_DAYS + 1));

      return [
        ...currentEvents,
        ...sourceDays.flatMap((day, dayIndex) =>
          currentEvents
            .filter((event) => toDayNumber(event.day) === day)
            .map((event) =>
              normalizeEvent({
                ...event,
                day: maxDay + dayIndex + 1,
              })
            )
        ),
      ];
    });
  };

  const handleRemoveEvent = (index) => {
    setEvents((currentEvents) =>
      currentEvents.filter((event, eventIndex) => eventIndex !== index)
    );
  };

  const handleSave = () => {
    onSave({
      ...mealPlan,
      events: events
        .map(normalizeEvent)
        .sort(
          (a, b) =>
            toDayNumber(a.day) - toDayNumber(b.day) ||
            getEventSortValue(a) - getEventSortValue(b)
        ),
    });
  };

  const getEventForCell = (day, timeOfDay) =>
    events.find(
      (event) => toDayNumber(event.day) === day && event.timeOfDay === timeOfDay
    );

  const handlePreviousDays = () => {
    setVisibleStartDay((currentStartDay) =>
      Math.max(1, currentStartDay - MIN_VISIBLE_DAYS)
    );
  };

  const openCreateMealDialog = (onCreated) => {
    setNewMeal({ title: '', desc: '', coverImages: [] });
    setCreateDialog({ open: true, onCreated });
  };

  const closeCreateMealDialog = () => {
    setCreateDialog({ open: false, onCreated: null });
  };

  const handleCreateMeal = async () => {
    if (!newMeal.title.trim()) {
      return;
    }
    const result = await createMeal({
      variables: {
        input: {
          title: newMeal.title.trim(),
          desc: newMeal.desc,
          status: 'DRAFT',
          tags: [],
          coverImages: newMeal.coverImages || [],
        },
      },
    });
    const createdMeal = result.data?.createMeal;
    if (createdMeal) {
      await refetchMeals();
      createDialog.onCreated?.(createdMeal);
    }
    closeCreateMealDialog();
  };

  const handleNextDays = () => {
    setVisibleStartDay((currentStartDay) =>
      Math.min(
        Math.max(1, days.length - MIN_VISIBLE_DAYS + 1),
        currentStartDay + MIN_VISIBLE_DAYS
      )
    );
  };

  const canShowPreviousDays = visibleDays[0] > 1;
  const canShowNextDays =
    visibleDays[visibleDays.length - 1] < days[days.length - 1];

  return (
    <Box>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        mb={2.5}
        spacing={2}
      >
        <ToggleButtonGroup
          exclusive
          onChange={(event, nextView) => nextView && setView(nextView)}
          size="small"
          value={view}
        >
          <ToggleButton value="calendar">
            <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
            Calendar View
          </ToggleButton>
          <ToggleButton value="table">
            <TableChartIcon fontSize="small" sx={{ mr: 1 }} />
            Table View
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2}>
          <Button
            endIcon={<KeyboardArrowDownIcon />}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
          >
            Templates
          </Button>
          <Button
            onClick={handleCopyWeek}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
          >
            Copy Week
          </Button>
          <Button
            onClick={handleAddDay}
            startIcon={<AddIcon />}
            variant="outlined"
          >
            Add Day
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: '#4338ca',
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Save Plan
          </Button>
        </Stack>
      </Stack>

      {events.length === 0 && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          No meals are scheduled yet.
        </Typography>
      )}

      {view === 'calendar' ? (
        <CalendarGrid
          canShowNextDays={canShowNextDays}
          canShowPreviousDays={canShowPreviousDays}
          days={visibleDays}
          events={events}
          getEventForCell={getEventForCell}
          onAddEvent={handleAddEvent}
          mealOptions={mealOptions}
          onCreateMeal={openCreateMealDialog}
          onMealSelect={handleMealSelect}
          onNextDays={handleNextDays}
          onPreviousDays={handlePreviousDays}
          rows={rows}
        />
      ) : (
        <TableEditor
          canShowNextDays={canShowNextDays}
          canShowPreviousDays={canShowPreviousDays}
          days={visibleDays}
          events={events}
          mealOptions={mealOptions}
          onAddEvent={handleAddEvent}
          onCreateMeal={openCreateMealDialog}
          onEventMealSelect={handleEventMealSelect}
          onEventTimeOfDayChange={handleEventTimeOfDayChange}
          onNextDays={handleNextDays}
          onPreviousDays={handlePreviousDays}
          onRemoveEvent={handleRemoveEvent}
          weekStart={weekStart}
        />
      )}
      <CreateMealDialog
        meal={newMeal}
        onChange={setNewMeal}
        onClose={closeCreateMealDialog}
        onCreate={handleCreateMeal}
        open={createDialog.open}
        saving={creatingMeal}
      />
    </Box>
  );
}

function CalendarGrid({
  canShowNextDays,
  canShowPreviousDays,
  days,
  getEventForCell,
  mealOptions,
  onAddEvent,
  onCreateMeal,
  onMealSelect,
  onNextDays,
  onPreviousDays,
  rows,
}) {
  return (
    <Card>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack alignItems="center" direction="row" spacing={2} sx={{ p: 2 }}>
          <IconButton
            disabled={!canShowPreviousDays}
            onClick={onPreviousDays}
            size="small"
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Button
            startIcon={<CalendarTodayIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            variant="outlined"
          >
            Days {days[0]} - {days[days.length - 1]}
          </Button>
          <IconButton
            disabled={!canShowNextDays}
            onClick={onNextDays}
            size="small"
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'grid',
            gridTemplateColumns: `188px repeat(${days.length}, minmax(150px, 1fr))`,
            minWidth: 900,
            overflowX: 'auto',
          }}
        >
          <HeaderCell>MEAL</HeaderCell>
          {days.map((day) => (
            <HeaderCell align="center" key={day}>
              Day {day}
            </HeaderCell>
          ))}

          {rows.map((timeOfDay) => {
            const label = TIME_LABELS[timeOfDay] || {
              title: timeOfDay,
              time: '',
            };

            return (
              <React.Fragment key={timeOfDay}>
                <Box
                  sx={{
                    borderTop: '1px solid',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    minHeight: 132,
                    p: 2,
                  }}
                >
                  <Typography variant="h5">{label.title}</Typography>
                  {label.time && (
                    <Typography
                      color="textSecondary"
                      sx={{ mt: 1 }}
                      variant="body2"
                    >
                      {label.time}
                    </Typography>
                  )}
                </Box>

                {days.map((day) => {
                  const event = getEventForCell(day, timeOfDay);

                  return (
                    <Box
                      key={`${day}-${timeOfDay}`}
                      sx={{
                        borderTop: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        minHeight: 132,
                        p: 1,
                      }}
                    >
                      <MealCard
                        meal={event?.meal || null}
                        mealOptions={mealOptions}
                        onChange={(value) =>
                          onMealSelect(day, timeOfDay, value)
                        }
                        onCreateMeal={onCreateMeal}
                      />
                    </Box>
                  );
                })}
              </React.Fragment>
            );
          })}

          <Box
            sx={{
              borderTop: '1px solid',
              borderRight: '1px solid',
              borderColor: 'divider',
              minHeight: 84,
            }}
          />
          {days.map((day) => (
            <Box
              key={`add-${day}`}
              sx={{
                borderTop: '1px solid',
                borderRight: '1px solid',
                borderColor: 'divider',
                minHeight: 84,
                p: 1,
              }}
            >
              <Button
                fullWidth
                onClick={() => onAddEvent(day)}
                startIcon={<AddIcon />}
                sx={{ borderStyle: 'dashed', height: 62 }}
                variant="outlined"
              >
                Add Meal
              </Button>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function HeaderCell({ align = 'left', children }) {
  return (
    <Box
      sx={{
        bgcolor: 'grey.50',
        borderRight: '1px solid',
        borderTop: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        fontWeight: 600,
        px: 2,
        py: 1.5,
        textAlign: align,
      }}
    >
      {children}
    </Box>
  );
}

function MealCard({ meal, mealOptions, onChange, onCreateMeal }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minHeight: 108,
        position: 'relative',
        px: 1,
        py: 2,
      }}
    >
      <MealSelect
        meal={meal}
        mealOptions={mealOptions}
        onChange={onChange}
        onCreateMeal={onCreateMeal}
        variant="card"
      />
    </Box>
  );
}

function TableEditor({
  canShowNextDays,
  canShowPreviousDays,
  days,
  events,
  mealOptions,
  onAddEvent,
  onCreateMeal,
  onEventMealSelect,
  onEventTimeOfDayChange,
  onNextDays,
  onPreviousDays,
  onRemoveEvent,
  weekStart,
}) {
  const indexedEvents = events.map((event, index) => ({ ...event, index }));
  const getDayEvents = (day) =>
    indexedEvents
      .filter((event) => toDayNumber(event.day) === day)
      .sort((a, b) => getEventSortValue(a) - getEventSortValue(b));
  const summary = events.reduce(
    (totals, event) => {
      const nutrition = nutritionFor(event);
      const hasNutrition = Object.values(nutrition).some(
        (value) => value != null
      );
      return {
        calories: totals.calories + (nutrition.calories || 0),
        protein: totals.protein + (nutrition.protein || 0),
        carbs: totals.carbs + (nutrition.carbs || 0),
        fats: totals.fats + (nutrition.fats || 0),
        count: totals.count + (hasNutrition ? 1 : 0),
      };
    },
    { calories: 0, protein: 0, carbs: 0, fats: 0, count: 0 }
  );
  const dayCount = Math.max(
    1,
    new Set(events.map((event) => toDayNumber(event.day))).size
  );

  return (
    <>
      <Card variant="outlined" sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ p: 2 }}
          >
            <Stack alignItems="center" direction="row" spacing={2}>
              <IconButton
                disabled={!canShowPreviousDays}
                onClick={onPreviousDays}
                size="small"
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Button
                startIcon={<CalendarTodayIcon />}
                variant="outlined"
                sx={{ px: 2.5, color: '#0f172a', fontWeight: 800 }}
              >
                {formatDateRange(days, weekStart)}
              </Button>
              <IconButton
                disabled={!canShowNextDays}
                onClick={onNextDays}
                size="small"
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Stack>

          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                borderCollapse: 'collapse',
                minWidth: 1120,
                tableLayout: 'fixed',
                width: '100%',
                '& th': {
                  bgcolor: '#f8fafc',
                  borderTop: '1px solid',
                  borderBottom: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#dbe3ee',
                  color: '#0f172a',
                  fontSize: 13,
                  fontWeight: 800,
                  px: 2,
                  py: 1.5,
                  textAlign: 'left',
                },
                '& td': {
                  borderBottom: '1px solid',
                  borderRight: '1px solid',
                  borderColor: '#dbe3ee',
                  color: '#0f172a',
                  fontSize: 15,
                  px: 2,
                  py: 1.35,
                  verticalAlign: 'middle',
                },
              }}
            >
              <Box component="colgroup">
                <Box component="col" sx={{ width: 150 }} />
                <Box component="col" sx={{ width: 180 }} />
                <Box component="col" sx={{ width: 190 }} />
                <Box component="col" sx={{ width: 260 }} />
                <Box component="col" sx={{ width: 120 }} />
                <Box component="col" sx={{ width: 120 }} />
                <Box component="col" sx={{ width: 120 }} />
                <Box component="col" sx={{ width: 120 }} />
                <Box component="col" sx={{ width: 110 }} />
              </Box>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th">DAY</Box>
                  <Box component="th">MEAL</Box>
                  <Box component="th">TIME</Box>
                  <Box component="th">MEAL / RECIPE</Box>
                  <Box component="th">CALORIES</Box>
                  <Box component="th">PROTEIN</Box>
                  <Box component="th">CARBS</Box>
                  <Box component="th">FATS</Box>
                  <Box component="th">ACTIONS</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {days.map((day) => {
                  const dayEvents = getDayEvents(day);
                  const rowCount = Math.max(dayEvents.length, 1);

                  if (dayEvents.length === 0) {
                    return (
                      <Box component="tr" key={day}>
                        <DayCell
                          day={day}
                          rowSpan={1}
                          onAddEvent={onAddEvent}
                          weekStart={weekStart}
                        />
                        <EmptyCell colSpan={8} />
                      </Box>
                    );
                  }

                  return dayEvents.map((event, rowIndex) => {
                    const nutrition = nutritionFor(event);
                    return (
                      <Box component="tr" key={`${day}-${event.index}`}>
                        {rowIndex === 0 && (
                          <DayCell
                            day={day}
                            rowSpan={rowCount}
                            onAddEvent={onAddEvent}
                            weekStart={weekStart}
                          />
                        )}
                        <Box component="td">
                          <MealTypeSelect
                            timeOfDay={event.timeOfDay}
                            onChange={(timeOfDay) =>
                              onEventTimeOfDayChange(event.index, timeOfDay)
                            }
                          />
                        </Box>
                        <Box component="td">
                          <Typography sx={{ color: '#334155' }}>
                            {TIME_LABELS[event.timeOfDay]?.time || '-'}
                          </Typography>
                        </Box>
                        <Box component="td">
                          <MealSelect
                            meal={event.meal || null}
                            mealOptions={mealOptions}
                            onChange={(selectedMeal) =>
                              onEventMealSelect(event.index, selectedMeal)
                            }
                            onCreateMeal={onCreateMeal}
                            variant="table"
                          />
                        </Box>
                        <NutritionCell>
                          {formatMacro(nutrition.calories, 'kcal')}
                        </NutritionCell>
                        <NutritionCell>
                          {formatMacro(nutrition.protein, 'g')}
                        </NutritionCell>
                        <NutritionCell>
                          {formatMacro(nutrition.carbs, 'g')}
                        </NutritionCell>
                        <NutritionCell>
                          {formatMacro(nutrition.fats, 'g')}
                        </NutritionCell>
                        <Box component="td">
                          <Stack direction="row" spacing={1}>
                            <IconButton color="primary" size="small">
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => onRemoveEvent(event.index)}
                              size="small"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  });
                })}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <WeeklySummary
        days={days}
        summary={summary}
        dayCount={dayCount}
        weekStart={weekStart}
      />
    </>
  );
}

function MealSelect({ meal, mealOptions, onChange, onCreateMeal, variant }) {
  return (
    <Stack spacing={1}>
      <Autocomplete
        getOptionLabel={(option) => option?.title || ''}
        isOptionEqualToValue={(option, value) => option?._id === value?._id}
        onChange={(event, value) => onChange(value)}
        options={mealOptions}
        size="small"
        value={meal}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select meal"
            variant={variant === 'table' ? 'standard' : 'outlined'}
            InputProps={{
              ...params.InputProps,
              disableUnderline: variant === 'table',
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                ...(params.inputProps?.style || {}),
                fontWeight: 700,
                textAlign: variant === 'card' ? 'center' : 'left',
              },
            }}
          />
        )}
      />
      <Button
        onClick={() => onCreateMeal((createdMeal) => onChange(createdMeal))}
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        sx={{
          alignSelf: variant === 'card' ? 'center' : 'flex-start',
          minWidth: 0,
        }}
      >
        Create Meal
      </Button>
    </Stack>
  );
}

function MealTypeSelect({ timeOfDay, onChange }) {
  return (
    <Stack alignItems="center" direction="row" spacing={1.5}>
      <MealDot timeOfDay={timeOfDay} />
      <Select
        disableUnderline
        fullWidth
        onChange={(event) => onChange(event.target.value)}
        size="small"
        value={timeOfDay}
        variant="standard"
        sx={{
          '& .MuiSelect-select': {
            fontWeight: 700,
            py: 0.25,
          },
        }}
      >
        {TIME_OF_DAY_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {TIME_LABELS[option]?.title || option}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

function CreateMealDialog({ meal, onChange, onClose, onCreate, open, saving }) {
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Create Meal</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Meal Name"
            onChange={(event) =>
              onChange({ ...meal, title: event.target.value })
            }
            required
            value={meal.title}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            onChange={(event) =>
              onChange({ ...meal, desc: event.target.value })
            }
            rows={4}
            value={meal.desc}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              Photo{' '}
              <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}>
                (optional)
              </Box>
            </Typography>
            <DirectUpload
              images={meal.coverImages || []}
              maxFiles={1}
              onError={() => {}}
              onRemove={() => onChange({ ...meal, coverImages: [] })}
              onSuccess={(uploadedResponse) =>
                onChange({
                  ...meal,
                  coverImages: uploadedResponse?.image
                    ? [uploadedResponse.image]
                    : [],
                })
              }
              style={{
                border: '1px dashed #cbd5e1',
                borderRadius: 8,
                minHeight: 120,
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={saving || !meal.title.trim()}
          onClick={onCreate}
          variant="contained"
          sx={{
            backgroundColor: '#4338ca',
            '&:hover': { backgroundColor: '#3730a3' },
          }}
        >
          Create Meal
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WeeklySummary({ days, summary, dayCount, weekStart }) {
  const hasNutrition = summary.count > 0;

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: '#dbe3ee', borderRadius: 1, mt: 1.5 }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
              Weekly Summary
            </Typography>
            <Typography sx={{ color: '#40536b', mt: 0.75 }}>
              {formatDateRange(days, weekStart)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={6} alignItems="center">
            <SummaryStat
              icon={<LocalFireDepartmentIcon sx={{ color: '#f97316' }} />}
              label="Avg. Calories"
              value={
                hasNutrition
                  ? `${Math.round(
                      summary.calories / dayCount
                    ).toLocaleString()} kcal`
                  : '-'
              }
            />
            <SummaryStat
              icon={<RestaurantIcon sx={{ color: '#39b824' }} />}
              label="Protein"
              value={
                hasNutrition
                  ? `${Math.round(summary.protein / dayCount)} g`
                  : '-'
              }
            />
            <SummaryStat
              icon={<QueryStatsIcon sx={{ color: '#4338ca' }} />}
              label="Carbs"
              value={
                hasNutrition ? `${Math.round(summary.carbs / dayCount)} g` : '-'
              }
            />
            <SummaryStat
              icon={<LocalFireDepartmentIcon sx={{ color: '#f59e0b' }} />}
              label="Fats"
              value={
                hasNutrition ? `${Math.round(summary.fats / dayCount)} g` : '-'
              }
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SummaryStat({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      {icon}
      <Box>
        <Typography sx={{ color: '#40536b', fontSize: 14 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{value}</Typography>
      </Box>
    </Stack>
  );
}

function NutritionCell({ children }) {
  return (
    <Box component="td">
      <Typography sx={{ fontWeight: 600 }}>{children}</Typography>
    </Box>
  );
}

function DayCell({ day, rowSpan, onAddEvent, weekStart }) {
  return (
    <Box component="td" rowSpan={rowSpan}>
      <Stack alignItems="flex-start" spacing={1.5}>
        <Typography sx={{ fontWeight: 800, fontSize: 19 }}>
          Day {day}
        </Typography>
        <Typography sx={{ color: '#40536b' }}>
          {formatDayDate(day, weekStart)}
        </Typography>
        <Button
          onClick={() => onAddEvent(day)}
          startIcon={<AddIcon />}
          variant="outlined"
          sx={{ mt: 1 }}
        >
          Add Meal
        </Button>
      </Stack>
    </Box>
  );
}

function EmptyCell({ colSpan }) {
  return (
    <Box component="td" colSpan={colSpan}>
      <Typography color="textSecondary" variant="body2">
        No meals scheduled.
      </Typography>
    </Box>
  );
}

function MealDot({ timeOfDay }) {
  const colorByTime = {
    BREAKFAST: '#f59e0b',
    'MORNING SNACK': '#39b824',
    BRUNCH: '#6366f1',
    LUNCH: '#1e88e5',
    'AFTERNOON SNACK': '#14b8a6',
    'AFTERNOON MEAL': '#64748b',
    DINNER: '#9333ea',
  };

  return (
    <Box
      sx={{
        bgcolor: colorByTime[timeOfDay] || 'text.secondary',
        borderRadius: '50%',
        height: 10,
        width: 10,
      }}
    />
  );
}
